import { Flex, Box, Text, useMediaQuery, Portal, useOutsideClick } from "@chakra-ui/react";
import { WithAuth } from "@/hoc";
import { useCharge, useChargeFilter } from "@/hooks";
import { ChargePaginator } from "@/models";
import { useEffect, useRef, useState } from "react";
import {
  formatCurrency,
  getColorBadgeStatus,
  getStatus,
  formatDate,
  omitProp,
  showToast,
} from "@/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert } from "@/components";
import { DynamicTable } from "@/components/DynamicTable";
import { ChargeTableFilter } from "@/components/ChargeTableFilter";
import { LocalDate } from "@js-joda/core";
import { Pagination2 } from "@/components/Pagination2";
import { ActionDrawerItems, DrawerList, Icon, IconButton, PageHeading, Pills, Tooltip } from "barte-design-system";
import { DotsThreeVerticalIcon, TrashIcon, EnvelopeIcon, WhatsappLogoIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
interface FilterState {
  status?: string | null
  customerName?: string | null
  customerDocument?: string | null
  customerEmail?: string | null
  title?: string | null
  expirationDateInitial?: LocalDate | null
  expirationDateFinal?: LocalDate | null
  paidDateInitial?: LocalDate | null
  paidDateFinal?: LocalDate | null
  paymentLink?: string | null
  valueMin?: number | null
  valueMax?: number | null
  paymentMethod?: string | null
  page?: number;
}

const Cobrancas = () => {
  const navigate = useNavigate();
  const cancelRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({} as ChargePaginator);
  const [deleteChargeModalState, setDeleteChargeModalState] = useState(false);
  const [chargeUUID, setChargeUUID] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [isLargerThan1080] = useMediaQuery("(min-width: 1590px)");
  const [pageSize, setPageSize] = useState(8);

  const {
    searchTerm,
    clearFilterState,
    searchTypeOptions,
    filterState,
    setFilterState,
    setSearchTerm,
    setSearchType,
    setHasAppliedFilters,
  } = useChargeFilter();

  const { getCharges, deleteCharge } = useCharge();
  const [loading, setLoading] = useState<boolean>(false);

  async function getChargesData(filterState?: FilterState) {
    setLoading(true)
    try {
      const params = {
        chargeFilterConfig: filterState?.status || "ALL",
        ...(filterState?.expirationDateInitial && {
          expirationDateInitial: filterState.expirationDateInitial.toString()
        }),
        ...(filterState?.expirationDateFinal && {
          expirationDateFinal: filterState.expirationDateFinal.toString()
        }),
        ...(filterState?.paidDateInitial && {
          paidDateInitial: filterState.paidDateInitial.toString()
        }),
        ...(filterState?.paidDateFinal && {
          paidDateFinal: filterState.paidDateFinal.toString()
        }),
        ...(filterState?.customerName && { customerName: filterState.customerName }),
        ...(filterState?.customerDocument && { customerDocument: filterState.customerDocument }),
        ...(filterState?.customerEmail && { customerEmail: filterState.customerEmail }),
        ...(filterState?.title && { title: filterState.title }),
        ...(filterState?.paymentLink && { paymentLink: filterState.paymentLink }),
        ...(filterState?.valueMin && { valueMin: filterState.valueMin }),
        ...(filterState?.valueMax && { valueMax: filterState.valueMax }),
        ...(filterState?.paymentMethod && { paymentMethod: filterState.paymentMethod })
      }


      const response = await getCharges(
        params,
        {
          size: pageSize,
          page: filterState.page - 1,
          sort: "expirationDate,DESC",
        }
      )

      setData(response.data)
    } catch (error) {
      if (!filterState?.expirationDateFinal) {
        showToast({
          message: "Por Favor, selecione uma data final.",
          type: "warning",
        })
        return
      }

      error?.response?.data?.errors?.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      })
    } finally {
      setLoading(false)
    }
  }

  function handleToggleDeleteChargeModal() {
    setDeleteChargeModalState(!deleteChargeModalState);
  }

  function handleGetChargeUUID(uuid: string) {
    setChargeUUID(uuid);
    setDeleteChargeModalState(!deleteChargeModalState);
  }

  async function handleDeleteCharge(uuid: string) {
    try {
      await deleteCharge(uuid);

      getChargesData();

      showToast({
        type: "success",
        message: "Sucesso na exclusão da cobrança.",
      });

      setDeleteChargeModalState(!deleteChargeModalState);
    } catch (error) {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        });
      });
    }
  }

  const cellFontSize = isLargerThan1080 ? "bodySm" : "bodyXs";

  const columns = [
    {
      title: "Cliente",
      dataIndex: "customerName",
      key: "customerName",
      sortable: true,
      render: (value: string, record: any) => (
        <Flex
          flexDirection="column"
          maxWidth={["200px", "200px", "200px", "250px", "250px", "100%"]}
          fontSize="bodySm"
        >
          <Tooltip label={value}>
            <Text noOfLines={1} fontSize={cellFontSize} fontWeight="500" color="content.primary">{value}</Text>
          </Tooltip>
          <Tooltip label={record.email}>
            <Text fontSize="captionSm" color="content.tertiary">{record.email}</Text>
          </Tooltip>
        </Flex>
      )
    },
    {
      title: "Cobrança",
      dataIndex: "title",
      key: "title",
      sortable: true,
      render: (title: string, record: any) => (
        <Text
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/cobrancas/${record.uuid}`);
          }}
          _hover={{
            cursor: "pointer",
          }}
          wordBreak="break-word"
          whiteSpace="normal"
          fontSize={cellFontSize}
          fontWeight="500"
          color="content.primary"
          isTruncated
        >
          {title}
        </Text>
      )
    },
    {
      key: "paidDate",
      title: "Pag.",
      dataIndex: "paidDate",
      tooltipMessage: "Data de pagamento",
      sortable: true,
      render: (paidDate, record) =>
      (<Text fontSize={cellFontSize} color="content.primary"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/cobrancas/${record.uuid}`);
        }}
        _hover={{
          cursor: "pointer",
        }}
      >
        {formatDate(paidDate)}
      </Text>)
      ,
    },
    {
      title: "Venc.",
      dataIndex: "expirationDate",
      key: "expirationDate",
      tooltipMessage: "Vencimento",
      sortable: true,
      sortFunction: (a, b) => {
        const dateA = new Date(a.expirationDate).getTime();
        const dateB = new Date(b.expirationDate).getTime();
        return dateA - dateB;
      },
      render: (date: string, record: any) => (
        <Text fontSize={cellFontSize} color="content.primary"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/cobrancas/${record.uuid}`);
          }}
          _hover={{
            cursor: "pointer",
          }}
        >
          {formatDate(date)}
        </Text>
      )
    },
    {
      key: "installments",
      title: "Parc.",
      dataIndex: "installments",
      tooltipMessage: "Parcelamento",
      sortable: true,
      render: (installments, record: any) =>
      (
        <Text w="100%" fontSize={cellFontSize} fontWeight="500" color="content.primary"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/cobrancas/${record.uuid}`);
          }}
          _hover={{
            cursor: "pointer",
          }}
        >
          {installments}
        </Text>
      )
      ,
    },
    {
      title: "Valor",
      dataIndex: "value",
      key: "value",
      sortable: true,
      render: (value: number, record: any) => (
        <Text fontSize={cellFontSize} color="content.primary"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/cobrancas/${record.uuid}`);
          }}
          _hover={{
            cursor: "pointer",
          }}
        >
          {formatCurrency({ value })}
        </Text>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sortable: true,
      render: (status: string, record: any) => (
        <Box maxWidth="160px"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/cobrancas/${record.uuid}`);
          }}
          _hover={{
            cursor: "pointer",
          }}
        >
          <Pills
            label={getStatus(status)}
            size="sm"
            state={getColorBadgeStatus(status)}
            variant="light"
          />
        </Box>
      )
    },
    {
      key: 'actions',
      title: '',
      dataIndex: 'uuid',
      align: "right" as const,
      render: (text, record) => (
        <Box position="relative"
          onClick={(e) => e.stopPropagation()}
        >

          <IconButton
            ref={(el) => {
              if (el) {
                buttonRefs.current.set(record.uuid, el);
              } else {
                buttonRefs.current.delete(record.uuid);
              }
            }}
            variant="outline"
            size="sm"
            round="soft"
            aria-label=""
            onClick={(e) => {
              e.stopPropagation();
              const button = buttonRefs.current.get(record.uuid);
              if (button) {
                const rect = button.getBoundingClientRect();
                setMenuPosition({
                  top: rect.top + window.scrollY,
                  right: window.innerWidth - rect.left + 8
                });
              }
              setOpenMenuId(openMenuId === record.uuid ? null : record.uuid);
            }}
          >
            <DotsThreeVerticalIcon weight="bold" />
          </IconButton>
          {openMenuId === record.uuid && record && menuPosition && (
            <Portal>
              <Box
                position="fixed"
                top={`${menuPosition.top}px`}
                right={`${menuPosition.right}px`}
                zIndex={1000}
                ref={menuRef}
              >
                <DrawerList>
                  <ActionDrawerItems title="" items={[
                    {
                      label: 'Abrir em nova aba',
                      variant: 'icon' as const,
                      icon: <Icon icon={ArrowSquareOutIcon} size={16} />,
                      onClick: () => {
                        setOpenMenuId(null);
                        setMenuPosition(null);
                        window.open(`/cobrancas/${record.uuid}`, '_blank');
                      }
                    },
                    {
                      label: 'Enviar email',
                      variant: 'icon' as const,
                      icon: <Icon icon={EnvelopeIcon} size={16} />,
                      onClick: () => {
                        setOpenMenuId(null);
                        setMenuPosition(null);
                        window.location.href = `mailto:${record?.email}`;
                      }
                    },
                    {
                      label: 'Enviar WhatsApp',
                      variant: 'icon' as const,
                      icon: <Icon icon={WhatsappLogoIcon} size={16} />,
                      onClick: () => {
                        setOpenMenuId(null);
                        setMenuPosition(null);
                        window.open(`https://api.whatsapp.com/send?phone=55${record?.phone}`, '_blank');
                      }
                    },
                    ...(record.status !== "PAID" && record.status !== "ABANDONNED"
                      && record.status !== "CANCELED" && record.status !== "REFUND"
                      && record.status !== "PAID_MANUALLY" && record.status !== "CHARGEBACK" ? [{
                        label: 'Deletar',
                        variant: 'icon' as const,
                        icon: <Icon icon={TrashIcon} size={16} />,
                        onClick: () => {
                          setOpenMenuId(null);
                          setMenuPosition(null);
                          handleGetChargeUUID(record.uuid);
                        }
                      }] : [])
                  ]} />
                </DrawerList>
              </Box>
            </Portal>
          )}
        </Box>
      )
    }

  ];

  useOutsideClick({
    ref: menuRef,
    handler: () => {
      setOpenMenuId(null);
      setMenuPosition(null);
    },
  });

  useEffect(() => {
    const queryFilters: FilterState = {
      status: searchParams.get("status") || "ALL",
      customerName: searchParams.get("customerName") || null,
      customerDocument: searchParams.get("customerDocument") || null,
      customerEmail: searchParams.get("customerEmail"),
      title: searchParams.get("title"),
      expirationDateInitial: searchParams.get("expirationDateInitial")
        ? LocalDate.parse(searchParams.get("expirationDateInitial")!)
        : null,
      expirationDateFinal: searchParams.get("expirationDateFinal")
        ? LocalDate.parse(searchParams.get("expirationDateFinal")!)
        : null,
      paidDateInitial: searchParams.get("paidDateInitial")
        ? LocalDate.parse(searchParams.get("paidDateInitial")!)
        : null,
      paidDateFinal: searchParams.get("paidDateFinal")
        ? LocalDate.parse(searchParams.get("paidDateFinal")!)
        : null,
      paymentLink: searchParams.get("paymentLink"),
      valueMin: searchParams.get("valueMin")
        ? Number(searchParams.get("valueMin"))
        : null,
      valueMax: searchParams.get("valueMax")
        ? Number(searchParams.get("valueMax"))
        : null,
      paymentMethod: searchParams.get("paymentMethod") || null,
      page: Number(searchParams.get("page")) || 1,
    };
    for (const option of searchTypeOptions) {
      const paramValue = searchParams.get(option.value);
      if (paramValue) {
        setSearchType(option.value);
        setSearchTerm(paramValue);
        break;
      }
    }
    const hasSearchTerm = searchTerm.trim() !== "";
    const formattedQueryFilter = omitProp(queryFilters, ["page"]);
    const formattedClearFilterState = omitProp(clearFilterState, ["page"]);
    const hasFilterChanges = JSON.stringify(formattedQueryFilter) !== JSON.stringify(formattedClearFilterState);
    setHasAppliedFilters(hasSearchTerm || hasFilterChanges);
    setFilterState(queryFilters);
    getChargesData(queryFilters);
  }, [searchParams, pageSize]);


  useEffect(() => {
    const currentPage = filterState.page || 1;
    const totalItems = data?.totalElements || 0;

    const newTotalPages = totalItems > 0 ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1;

    let newPage = currentPage;
    if (currentPage > newTotalPages && newTotalPages > 0) {
      newPage = newTotalPages;
    }

    const params = new URLSearchParams(searchParams);
    params.set("page", String(newPage));
    params.set("size", pageSize.toString());
    setSearchParams(params);

    if (newPage !== currentPage) {
      setFilterState({ ...filterState, page: newPage });
    }
  }, [pageSize]);

  useEffect(() => {
    if (filterState.page === undefined) return
    handleFilterChange(filterState);
  }, [filterState.page])
  function updateFiltersInURL(filters: Partial<FilterState>) {
    const params = new URLSearchParams(searchParams);
    params.delete("page");
    Object.entries(filters).forEach(([key, value]) => {
      if (key === "paymentMethod" && value === "ALL") {
        params.delete(key);
        return
      }
      if (key === "page") return;
      if (value) {
        params.set(key, value.toString());
      } else {
        params.delete(key);
      }
    });
    if (filters.page) {
      params.set("page", String((filters.page)));
    }
    setSearchParams(params);
  }
  const handleFilterChange = (filterState: FilterState) => {
    setFilterState(filterState);
    updateFiltersInURL(filterState);
    getChargesData(filterState);
  };

  return (
    <Flex
      flexDir="column"
      w="100%"
      gap="1rem"
    >
      <PageHeading
        title="Cobranças"
      />
      <Box letterSpacing="tighter">
        <Alert
          closeButtonText="Não"
          confirmButtonText="Sim"
          isConfirmAlert={false}
          isOpen={deleteChargeModalState}
          onConfirm={() => handleDeleteCharge(chargeUUID)}
          onClose={handleToggleDeleteChargeModal}
          title="Tem certeza que seja deletar essa cobrança?"
          messageBody="Essa ação não poderá ser desfeita"
          leastDestructiveRef={cancelRef}
        />
        <Flex
          flexDir="column"
          gap="1rem"
        >
          <ChargeTableFilter
            onFilterChange={handleFilterChange}
          />
          <DynamicTable
            data={data?.content}
            onRowClick={(record) => navigate(`/cobrancas/${record?.uuid}`)}
            columns={columns}
            loading={loading}
            rowKey={(record) => record.uuid}
            onRow={(record) => ({
              onClick: () => navigate(`/cobrancas/${record?.uuid}`)
            })}
            locale={{ emptyText: "Sem dados de cobranças" }}
            pagination={{
              currentPage: filterState.page,
              totalPages: data?.totalPages,
              pageSize: pageSize,
              onPageSizeChange: size => {
                setPageSize(size);
              },
              onPageChange: (page) => setFilterState({ ...filterState, page: page }),
              totalItems: data?.totalElements

            }}
          />

        </Flex>
      </Box>
    </Flex>
  );
};

export default WithAuth(Cobrancas);
